import React, { type PropsWithChildren, useMemo } from "react";
import { Platform, SectionList, StyleSheet } from "react-native";
import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";

import { isWeb } from "@stepapp/core";

import { spacing } from "../../styles";
import Block from "../Block";
import Gradient from "../Gradient";
import { Image } from "../Image";
import ParallaxHeader from "./ParallaxHeader";

const PARALLAX_HEADER_HEIGHT = 314;

const AnimatedSectionList = Animated.createAnimatedComponent(SectionList);

type Props = {
  backgroundImage?: string;
  imageHeight?: number;
  bodyTopPadding?: number;
  goBack: () => void;
};

const MIN_SCALE = 1;
const MAX_SCALE = 2;

const ParallaxScrollView = ({
  goBack,
  children,
  backgroundImage,
  imageHeight = PARALLAX_HEADER_HEIGHT,
  bodyTopPadding = imageHeight,
}: PropsWithChildren<Props>) => {
  const scrollY = useSharedValue(0);
  const sectionListZIndex = useAnimatedStyle(() => ({
    zIndex: Math.min(Math.max(Math.abs(scrollY.value), 1), 2),
  }));

  const scrollHandler = useAnimatedScrollHandler((event) => {
    scrollY.value = event.contentOffset.y;
  });

  const sections = Array.isArray(children)
    ? children.filter(Boolean).map((component) => ({
        data: [component.type.displayName],
        renderItem: () => component,
      }))
    : // @ts-expect-error
      [{ data: [children?.type?.displayNam], renderItem: () => children }];

  const headerStyles = useAnimatedStyle(() => {
    const clamp = (value: number, lowerBound: number, upperBound: number) =>
      Math.min(Math.max(value, lowerBound), upperBound);

    const translateY = clamp(scrollY.value * -0.65, -1000, 0);
    const scale = clamp(MIN_SCALE + scrollY.value / -(imageHeight / 2), MIN_SCALE, MAX_SCALE);

    return {
      transform: [{ translateY }, { scale }],
    };
  });

  const headerImageStyle = useMemo(
    () => ({
      height: isWeb ? imageHeight * 1.25 : imageHeight,
      width: spacing.deviceWidth,
      ...(isWeb && spacing.isBigScreen && { borderRadius: spacing.radius * 2 }),
    }),
    [imageHeight],
  );

  const bodyStyle = useMemo(
    () => ({ top: -spacing.unit * 3, paddingTop: bodyTopPadding }),
    [bodyTopPadding],
  );

  return (
    <>
      <ParallaxHeader
        goBack={goBack}
        interpolateAddition={imageHeight - bodyTopPadding}
        scrollY={scrollY}
      />
      <Animated.View style={[styles.header, headerStyles]}>
        <Block style={headerImageStyle}>
          <Image
            {...headerImageStyle}
            contentFit="cover"
            source={{ uri: backgroundImage }}
            style={headerImageStyle}
          />
        </Block>
        <Gradient direction="top" height={imageHeight} />
      </Animated.View>

      <AnimatedSectionList
        contentContainerStyle={bodyStyle}
        keyExtractor={(_item, index: number) => index.toString()}
        onScroll={scrollHandler}
        scrollEventThrottle={1}
        sections={sections}
        stickySectionHeadersEnabled={false}
        style={sectionListZIndex}
        testID="parallaxScrollView"
      />
    </>
  );
};

export default React.memo<Props>(ParallaxScrollView);

const styles = StyleSheet.create({
  header: {
    ...Platform.select({
      web: {
        borderTopLeftRadius: spacing.isBigScreen ? spacing.radius * 1 : 0,
        borderTopRightRadius: spacing.isBigScreen ? spacing.radius * 1 : 0,
        marginTop: spacing.unit * -10,
        overflow: "hidden",
      },
      default: {},
    }),
    position: "absolute",
    zIndex: 1,
  },
});
